import { useNFTStakeContract } from './useContract'
import { useSingleCallResult } from '../state/multicall/hooks'
import { ChainId, JSBI, Token, TokenAmount } from '@yetiswap/sdk'
import { useGetNFTPrice } from './useNFTMarketPlace'
import { YTS } from '../constants'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useCallback } from 'react'
import { calculateGasMargin } from '../utils'
import { TransactionResponse } from '@ethersproject/providers'

const YTSToken: Token = YTS[ChainId.AVALANCHE]

export function useNFTStakeRewardPerToken(address: string): string {
  const contract = useNFTStakeContract(address)
  const rewardPertokenInfo = useSingleCallResult(contract, 'rewardPerTokenStored', [])
  const rewardRate =
    rewardPertokenInfo && !rewardPertokenInfo.loading && rewardPertokenInfo.result
      ? rewardPertokenInfo.result[0].toString()
      : '0'
  return useGetNFTPrice(rewardRate, YTSToken, true)
}

export function useEarnedAmount(address: string, account?: string | null): TokenAmount {
  const contract = useNFTStakeContract(address)
  const earnedAmountState = useSingleCallResult(contract, 'earned', [account ?? undefined])
  return new TokenAmount(YTSToken, JSBI.BigInt(earnedAmountState?.result?.[0] ?? 0))
}

export function useNFTStakeTokensOfAccount(address: string, account?: string | null): number[] {
  const contract = useNFTStakeContract(address)
  const tokensOfAccountInfo = useSingleCallResult(contract, 'tokensOfAccount', [account ?? undefined])
  const tokensOfAccountList =
    tokensOfAccountInfo && !tokensOfAccountInfo.loading && tokensOfAccountInfo.result
      ? tokensOfAccountInfo.result[0]
      : []

  return tokensOfAccountList
}

export function useNFTStakeRewardRate(
  address: string,
  totalStakedAmount: TokenAmount,
  stakedAmount: number
): {
  totalRewardRate: TokenAmount
  rewardRate: TokenAmount
} {
  const contract = useNFTStakeContract(address)
  const rewardRateState = useSingleCallResult(contract, 'rewardRate', [])
  const periodFinishState = useSingleCallResult(contract, 'periodFinish', [])

  const periodFinishMs = periodFinishState.result?.[0]?.mul(1000)?.toNumber()

  // periodFinish will be 0 immediately after a reward contract is initialized
  const isPeriodFinished = periodFinishMs === 0 ? false : periodFinishMs < Date.now()

  const totalRewardRate = new TokenAmount(
    YTSToken,
    JSBI.BigInt(isPeriodFinished ? 0 : rewardRateState.result?.[0] ?? 0)
  )

  const getHypotheticalRewardRate = (): TokenAmount => {
    return new TokenAmount(
      YTSToken,
      JSBI.greaterThan(totalStakedAmount.raw, JSBI.BigInt(0))
        ? JSBI.divide(JSBI.multiply(totalRewardRate.raw, JSBI.BigInt(stakedAmount)), totalStakedAmount.raw)
        : JSBI.BigInt(0)
    )
  }

  return {
    totalRewardRate,
    rewardRate: getHypotheticalRewardRate()
  }
}

export function useNFTStakeTotalStaked(address: string): TokenAmount {
  const contract = useNFTStakeContract(address)
  const totalStakingInfo = useSingleCallResult(contract, 'totalSupply', [])

  return new TokenAmount(
    YTSToken,
    JSBI.BigInt(totalStakingInfo && totalStakingInfo.result?.[0] ? totalStakingInfo.result?.[0] : 0)
  )
}

export function useStakeNFT(tokenId: number, address: string): [() => Promise<void>] {
  const contract = useNFTStakeContract(address)
  const addTransaction = useTransactionAdder()

  const stakeNFT = useCallback(async (): Promise<void> => {
    if (tokenId === undefined || tokenId === null || !contract) return

    const args = [tokenId]

    return await contract.estimateGas['stake'](...args, {}).then(estimatedGasLimit => {
      return contract
        .stake(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          return response.wait(1).then(() => {
            addTransaction(response, {
              summary: 'NFT Stake Successfully'
            })
          })
        })
    })
  }, [tokenId, addTransaction, contract])

  return [stakeNFT]
}

export function useNFTStakeGetReward(address: string): [() => Promise<void>] {
  const contract = useNFTStakeContract(address)
  const addTransaction = useTransactionAdder()

  const rewardClaim = useCallback(async (): Promise<void> => {
    if (!contract) return
    const args: any[] = []
    return await contract.estimateGas['getReward'](...args, {}).then(estimatedGasLimit => {
      return contract
        .getReward(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          return response.wait(1).then(() => {
            addTransaction(response, {
              summary: 'Reward Claimed Successfully'
            })
          })
        })
    })
  }, [addTransaction, contract])

  return [rewardClaim]
}

export function useNFTStakeExitAll(address: string): [() => Promise<void>] {
  const contract = useNFTStakeContract(address)
  const addTransaction = useTransactionAdder()

  const exitAll = useCallback(async (): Promise<void> => {
    if (!contract) return
    const args: any[] = []
    return await contract.estimateGas['exitAll'](...args, {}).then(estimatedGasLimit => {
      return contract
        .exitAll(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          return response.wait(1).then(() => {
            addTransaction(response, {
              summary: 'Withdraw All & Claim Successfully'
            })
          })
        })
    })
  }, [addTransaction, contract])

  return [exitAll]
}

export function useUnstakeNFT(tokenId: number, address: string): [() => Promise<void>] {
  const contract = useNFTStakeContract(address)
  const addTransaction = useTransactionAdder()

  const unstakeNFT = useCallback(async (): Promise<void> => {
    if (tokenId === undefined || tokenId === null || !contract) return

    const args = [tokenId]

    return await contract.estimateGas['exit'](...args, {}).then(estimatedGasLimit => {
      return contract
        .exit(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) })
        .then((response: TransactionResponse) => {
          return response.wait(1).then(() => {
            addTransaction(response, {
              summary: 'NFT Unstake Successfully'
            })
          })
        })
    })
  }, [tokenId, addTransaction, contract])

  return [unstakeNFT]
}
